import { Button, Input, InputProps, Tooltip } from 'antd'
import classNames from 'classnames'
import IconHidePassword from 'components/icons/IconHidePassword'
import IconShowPassword from 'components/icons/IconShowPassword'
import IconTooltip from 'components/icons/IconTooltip'
import React, { ReactNode, useState } from 'react'
import { Control, Controller, ControllerRenderProps } from 'react-hook-form'

interface BaseInputPasswordProps extends InputProps {
  name: string
  label: string
  className?: string
  control: Control<any>
  defaultValue?: string
  maxLength?: number
  eyeOutlinedIcon?: boolean
  infoNode?: ReactNode
}

const BaseInputPassword: React.FC<BaseInputPasswordProps> = ({
  name,
  label,
  className,
  control,
  maxLength = 256,
  defaultValue,
  infoNode,
  ...textFieldProps
}) => {
  const defaultValueInput = defaultValue !== undefined ? defaultValue : ''

  // Trim Field Input
  const handleMouseOut = (
    event: React.FocusEvent<HTMLInputElement, Element>,
    field: ControllerRenderProps<any, string>
  ) => {
    const trimmedValue = event.target.value.trim()
    return field.onChange(trimmedValue)
  }
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible)
  }
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      togglePasswordVisibility()
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValueInput}
      render={({ field, fieldState: { error } }) => {
        return (
          <>
            <label className='text-sm text-neutral-700 mb-1 font-medium flex items-center' htmlFor={name}>
              {label}
              {infoNode && (
                <Tooltip title={infoNode}>
                  <Button className='w-4 min-w-4 h-4 min-h-4 border-none ml-1' shape='circle' icon={<IconTooltip />} />
                </Tooltip>
              )}
            </label>
            <div className='relative flex items-center justify-center rounded-lg'>
              <Input
                id={name}
                type={isPasswordVisible ? 'text' : 'password'}
                maxLength={maxLength}
                className={classNames(
                  ' pr-12 mt-1 h-10 p-3 pl-4 rounded-lg placeholder:text-neutral-300 text-neutral-700 ',
                  className
                )}
                {...textFieldProps}
                {...field}
                onBlur={(event) => handleMouseOut(event, field)}
              />
              <span
                className='absolute top-4 right-3 cursor-pointer'
                onClick={togglePasswordVisibility}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                role='button'
              >
                {isPasswordVisible ? <IconHidePassword /> : <IconShowPassword />}
              </span>
            </div>

            {error && <div className='text-error text-sm pt-1'>{error.message}</div>}
          </>
        )
      }}
    />
  )
}

export default BaseInputPassword
